import React, { useState, useMemo, useEffect } from "react";
import { Check, Loader2, ShieldCheck, FileText, Lock, Clock, Users, Trash2 } from "lucide-react";
import { PageHero, Reveal, CTAButton } from "@/components/primitives";
import { createOrder, verifyPayment, loadRazorpay } from "@/lib/api";
import { fbTrack, fbAdvancedMatch, sendCapi } from "@/lib/pixel";
import { toast } from "sonner";
import { REG_TARIFF, REG_WINDOW_LABELS, currentRegWindow, EVENT, getGroupTier } from "@/data/site";
import GroupDiscountCard from "@/components/GroupDiscountCard";
import { cn } from "@/lib/utils";

const INTERESTS = [
  "Producer / Distiller", "Sugar Mill", "Oil Marketing Company", "Automaker / OEM",
  "Technology / Equipment / EPC", "Investor / Bank / Fund", "Government / Policy",
  "Consultant / Advisory", "Media", "Academia / Student", "Other",
];

const fmt = (n, cur) => (cur === "INR" ? "₹" + n.toLocaleString("en-IN") : "$" + n.toLocaleString("en-US"));
const inputCls = "w-full rounded-xl border border-border bg-white px-4 py-3 text-sm text-navy outline-none transition-colors focus:border-emerald2";

function Card({ step, title, children }) {
  return (
    <div className="rounded-3xl border border-border bg-white p-7 shadow-sm md:p-8">
      <div className="mb-5 flex items-center gap-3">
        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-navy text-sm font-bold text-white">{step}</span>
        <h2 className="font-display text-xl font-semibold text-navy">{title}</h2>
      </div>
      {children}
    </div>
  );
}

function Row({ k, v, muted }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className={muted ? "text-xs text-navy/45" : "text-navy/70"}>{k}</span>
      {v && <span className="font-medium text-navy">{v}</span>}
    </div>
  );
}

export default function Register() {
  const windowIdx = useMemo(() => currentRegWindow(), []);
  const win = REG_WINDOW_LABELS[windowIdx];
  const [nationality, setNationality] = useState("indian");
  const [passKey, setPassKey] = useState("conference");
  const [quantity, setQuantity] = useState(1);
  const [form, setForm] = useState({ fullName: "", email: "", phone: "", company: "", interest: "" });
  // Delegates 2..N (Delegate 1 = primary contact above). Each: {fullName, email, phone, role}
  const [extras, setExtras] = useState([]);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [order, setOrder] = useState(null);

  // Resize extras when quantity changes
  useEffect(() => {
    setExtras((prev) => {
      const need = Math.max(0, quantity - 1);
      if (prev.length === need) return prev;
      if (prev.length < need) {
        return [...prev, ...Array(need - prev.length).fill(0).map(() => ({ fullName: "", email: "", phone: "", role: "" }))];
      }
      return prev.slice(0, need);
    });
  }, [quantity]);

  const setExtra = (i, k, v) => setExtras((prev) => prev.map((x, idx) => idx === i ? { ...x, [k]: v } : x));

  const currency = nationality === "indian" ? "INR" : "USD";
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const price = useMemo(() => {
    const t = REG_TARIFF[passKey];
    const perDelegate = (nationality === "indian" ? t.inr : t.usd)[windowIdx];
    const tier = getGroupTier(quantity);
    const gross = perDelegate * quantity;
    const discount = Math.round(gross * tier.discount / 100);
    const net = gross - discount;
    const gst = nationality === "indian" ? Math.round(net * 0.18) : 0;
    const total = net + gst;
    return { perDelegate, gross, discount, net, gst, total, tier };
  }, [passKey, nationality, windowIdx, quantity]);

  const primaryValid = form.fullName && form.email && form.phone && form.company && form.interest;
  const extrasValid = extras.every((d) => d.fullName && d.email);
  const valid = primaryValid && extrasValid;

  const handlePay = async (e) => {
    if (e) e.preventDefault();
    if (!primaryValid) { toast.error("Please complete the primary delegate details."); return; }
    if (!extrasValid) { toast.error(`Please complete name & email for all ${quantity} delegates.`); return; }
    setLoading(true);
    const t = REG_TARIFF[passKey];
    const [firstName, ...lastParts] = form.fullName.trim().split(/\s+/);
    const lastName = lastParts.join(" ");
    const country = nationality === "foreign" ? "International" : "India";

    // Build attendee roster (primary + extras)
    const attendees = [
      { fullName: form.fullName, email: form.email, phone: form.phone, role: "" },
      ...extras.map((d) => ({ fullName: d.fullName, email: d.email, phone: d.phone || "", role: d.role || "" })),
    ];

    // Meta Pixel: Advanced Matching + InitiateCheckout
    fbAdvancedMatch({ email: form.email, phone: form.phone, firstName, lastName });
    fbTrack("InitiateCheckout", { value: price.total, currency: currency, num_items: 1, content_name: t.label });

    const basePayload = {
      name: form.fullName,
      email: form.email,
      phone: form.phone,
      organisation: form.company,
      interest: form.interest,
      country,
      pass_type: t.label,
      delegate_type: nationality === "indian" ? "Indian" : "International",
      pricing_window: win.label,
      currency,
      base_amount: price.net,
      gst_amount: price.gst,
      total_amount: price.total,
      quantity,
      group_tier: price.tier.key,
      discount_percent: price.tier.discount,
      gross_amount: price.gross,
      discount_amount: price.discount,
      attendees,
    };
    try {
      const ok = await loadRazorpay();
      if (!ok) { toast.error("Could not load payment gateway. Please try again."); setLoading(false); return; }

      const orderData = await createOrder(basePayload);

      const rzp = new window.Razorpay({
        key: orderData.key_id,
        amount: orderData.amount,
        currency: orderData.currency,
        order_id: orderData.order_id,
        name: "Bharat Ethanol Forum 2026",
        description: `${t.label} · ${nationality === "indian" ? "Indian" : "International"} delegate`,
        prefill: { name: form.fullName, email: form.email, contact: form.phone },
        notes: { pass_type: t.label },
        theme: { color: "#0f5132" },
        handler: async (resp) => {
          try {
            await verifyPayment({
              submission_id: orderData.submission_id,
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            // Meta Pixel: Purchase + CompleteRegistration (browser + CAPI, deduped by event_id)
            const ud = { email: form.email, phone: form.phone, first_name: firstName, last_name: lastName };
            const purchaseCustom = {
              value: price.total, currency: currency,
              content_name: "Bharat Ethanol Forum 2026 - Delegate Pass",
              contents: [{ id: passKey, quantity: quantity }], num_items: quantity,
            };
            fbTrack("Purchase", purchaseCustom, orderData.submission_id);
            sendCapi({ event_name: "Purchase", event_id: orderData.submission_id, user_data: ud, custom_data: purchaseCustom });
            fbTrack("CompleteRegistration", { content_name: "Bharat Ethanol Forum 2026 - Delegate", status: true }, `reg_${orderData.submission_id}`);
            sendCapi({ event_name: "CompleteRegistration", event_id: `reg_${orderData.submission_id}`, user_data: ud, custom_data: { content_name: "Bharat Ethanol Forum 2026 - Delegate", status: true } });

            setOrder({ passLabel: t.label, currency, quantity, paymentId: resp.razorpay_payment_id, gross: price.gross, discount: price.discount, gst: price.gst, total: price.total, tier: price.tier });
            setDone(true);
            toast.success("Payment successful! Your seat is confirmed.");
          } catch {
            toast.error("Payment captured but verification failed. We'll email you to confirm.");
          } finally {
            setLoading(false);
          }
        },
        modal: { ondismiss: () => setLoading(false) },
      });
      rzp.on("payment.failed", () => { toast.error("Payment failed. Please try again."); setLoading(false); });
      rzp.open();
    } catch {
      toast.error("Something went wrong. Please try again.");
      setLoading(false);
    }
  };

  if (done && order) {
    return (
      <>
        <PageHero overline="Register" title="You're on the list." subtitle={`${EVENT.dates} · ${EVENT.venue}.`} />
        <section className="bg-parchment py-24">
          <div className="mx-auto max-w-xl px-6 text-center">
            <Reveal>
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald2 text-white"><Check className="h-8 w-8" /></div>
              <h2 className="mt-6 font-display text-2xl font-semibold text-navy">Payment successful — your seat is confirmed.</h2>
              <p className="mt-3 text-navy/65">
                We&apos;ve confirmed your <strong>{order.passLabel}</strong> {order.quantity > 1 && <>× <strong>{order.quantity}</strong> </>}for <strong>{fmt(order.total, order.currency)}</strong>. A GST invoice and confirmation will be emailed to you shortly.
              </p>
              {order.paymentId && <p className="mt-2 text-xs text-navy/45">Payment ID: {order.paymentId}</p>}
              <div className="mx-auto mt-6 max-w-sm rounded-2xl border border-border bg-white p-5 text-left text-sm">
                <Row k={`${order.passLabel} × ${order.quantity}`} v={fmt(order.gross, order.currency)} />
                {order.discount > 0 && <div className="mt-2 flex items-center justify-between text-emeraldDeep">
                  <span className="font-medium">{order.tier.label} discount ({order.tier.discount}%)</span>
                  <span className="font-semibold">− {fmt(order.discount, order.currency)}</span>
                </div>}
                {order.gst > 0 && <div className="mt-2"><Row k="GST (18%)" v={fmt(order.gst, order.currency)} /></div>}
                <div className="mt-2 border-t border-border pt-2"><Row k="Total" v={fmt(order.total, order.currency)} /></div>
              </div>
              <CTAButton to="/" variant="emerald" className="mt-8">Back to home</CTAButton>
            </Reveal>
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      <PageHero overline="Register" title="Reserve your delegate pass." subtitle={`${EVENT.dates} · ${EVENT.venue}. A 60-second checkout.`} />

      <section className="bg-parchment py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="grid gap-8 lg:grid-cols-12 lg:items-start">
            {/* FORM */}
            <form onSubmit={handlePay} className="space-y-8 lg:col-span-7">
              <Card step="1" title="Delegate type">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {[
                    ["indian", "Indian delegate", "Billed in ₹ INR · 18% GST"],
                    ["foreign", "International delegate", "Billed in $ USD · no GST"],
                  ].map(([k, t, s]) => (
                    <button type="button" key={k} data-testid={`nat-${k}`} onClick={() => setNationality(k)}
                      className={cn("rounded-2xl border-2 p-4 text-left transition-all", nationality === k ? "border-emerald2 bg-emerald2/5" : "border-border hover:border-emerald2/40")}>
                      <p className="font-semibold text-navy">{t}</p>
                      <p className="mt-1 text-xs text-navy/55">{s}</p>
                    </button>
                  ))}
                </div>
              </Card>

              <Card step="2" title="Choose your pass">
                <div className="space-y-3">
                  {Object.entries(REG_TARIFF).map(([key, t]) => {
                    const base = (nationality === "indian" ? t.inr : t.usd)[windowIdx];
                    return (
                      <button type="button" key={key} data-testid={`pass-${key}`} onClick={() => setPassKey(key)}
                        className={cn("flex w-full items-center justify-between gap-4 rounded-2xl border-2 p-5 text-left transition-all", passKey === key ? "border-emerald2 bg-emerald2/5" : "border-border hover:border-emerald2/40")}>
                        <div className="flex items-start gap-3">
                          <span className={cn("mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2", passKey === key ? "border-emerald2 bg-emerald2 text-white" : "border-border")}>
                            {passKey === key && <Check className="h-3 w-3" />}
                          </span>
                          <div>
                            <p className="font-display text-lg font-semibold text-navy">{t.label}</p>
                            <p className="mt-1 text-sm text-navy/55">{t.sub}</p>
                          </div>
                        </div>
                        <div className="shrink-0 text-right">
                          <p className="font-display text-lg font-semibold text-emeraldDeep">{fmt(base, currency)}</p>
                          {nationality === "indian" && <p className="text-xs text-navy/40">+ GST</p>}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </Card>

              {/* SQUAD / GROUP DISCOUNT */}
              <GroupDiscountCard
                quantity={quantity}
                setQuantity={setQuantity}
                basePerDelegate={price.perDelegate}
                currency={currency}
                priceBreakdown={{ gross: price.gross, discount: price.discount, net: price.net, gst: price.gst, total: price.total }}
              />

              <Card step="3" title={quantity === 1 ? "Your details" : `Delegate details · ${quantity} attendees`}>
                <div className="mb-4 flex items-center gap-2 rounded-xl bg-emerald2/5 border border-emerald2/20 px-3 py-2 text-xs text-navy/70">
                  <Users className="h-3.5 w-3.5 text-emerald2 shrink-0" />
                  {quantity === 1
                    ? "Fill your details — this delegate's badge and GST invoice will be issued to you."
                    : <span>Delegate <strong className="text-navy">#1</strong> is the <strong className="text-navy">primary contact</strong> — GST invoice + confirmations go to this person.</span>}
                </div>

                {/* Primary delegate */}
                {quantity > 1 && (
                  <div className="mb-2 flex items-center gap-2">
                    <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-emerald2 text-[10px] font-bold text-white">1</span>
                    <span className="text-sm font-semibold text-navy">Primary contact · Delegate 1</span>
                  </div>
                )}
                <div className="grid gap-4 sm:grid-cols-2">
                  <input data-testid="reg-fullname" className={inputCls} placeholder="Full name *" value={form.fullName} onChange={(e) => set("fullName", e.target.value)} />
                  <input data-testid="reg-email" type="email" className={inputCls} placeholder="Work email *" value={form.email} onChange={(e) => set("email", e.target.value)} />
                  <input data-testid="reg-phone" className={inputCls} placeholder="Phone (with country code) *" value={form.phone} onChange={(e) => set("phone", e.target.value)} />
                  <input data-testid="reg-company" className={inputCls} placeholder="Company / Organisation *" value={form.company} onChange={(e) => set("company", e.target.value)} />
                  <select data-testid="reg-interest" className={cn(inputCls, "sm:col-span-2")} value={form.interest} onChange={(e) => set("interest", e.target.value)}>
                    <option value="">Area of interest / sector *</option>
                    {INTERESTS.map((i) => <option key={i} value={i}>{i}</option>)}
                  </select>
                </div>

                {/* Additional delegates */}
                {extras.length > 0 && (
                  <div className="mt-7 space-y-5">
                    <div className="flex items-center gap-2 border-t border-border pt-6">
                      <div className="h-px flex-1 bg-border" />
                      <span className="text-[10px] font-bold uppercase tracking-widest text-navy/50">Additional delegates ({extras.length})</span>
                      <div className="h-px flex-1 bg-border" />
                    </div>
                    {extras.map((d, i) => (
                      <div key={i} data-testid={`delegate-${i + 2}`} className="rounded-2xl border border-border bg-parchment/40 p-5">
                        <div className="mb-3 flex items-center gap-2">
                          <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-navy text-[10px] font-bold text-white">{i + 2}</span>
                          <span className="text-sm font-semibold text-navy">Delegate {i + 2}</span>
                          <button
                            type="button"
                            onClick={() => setQuantity(quantity - 1)}
                            className="ml-auto flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium text-navy/50 hover:bg-rose-50 hover:text-rose-600 transition"
                            data-testid={`remove-delegate-${i + 2}`}
                          >
                            <Trash2 className="h-3 w-3" /> Remove
                          </button>
                        </div>
                        <div className="grid gap-3 sm:grid-cols-2">
                          <input
                            data-testid={`del${i + 2}-fullname`}
                            className={inputCls}
                            placeholder="Full name *"
                            value={d.fullName}
                            onChange={(e) => setExtra(i, "fullName", e.target.value)}
                          />
                          <input
                            data-testid={`del${i + 2}-email`}
                            type="email"
                            className={inputCls}
                            placeholder="Work email *"
                            value={d.email}
                            onChange={(e) => setExtra(i, "email", e.target.value)}
                          />
                          <input
                            data-testid={`del${i + 2}-phone`}
                            className={inputCls}
                            placeholder="Phone (optional)"
                            value={d.phone}
                            onChange={(e) => setExtra(i, "phone", e.target.value)}
                          />
                          <input
                            data-testid={`del${i + 2}-role`}
                            className={inputCls}
                            placeholder="Role / Designation (optional)"
                            value={d.role}
                            onChange={(e) => setExtra(i, "role", e.target.value)}
                          />
                        </div>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => setQuantity(quantity + 1)}
                      data-testid="add-another-delegate"
                      className="flex w-full items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-emerald2/40 bg-emerald2/[0.03] py-3 text-sm font-semibold text-emeraldDeep transition hover:bg-emerald2/10"
                    >
                      + Add another delegate
                    </button>
                  </div>
                )}
              </Card>
            </form>

            {/* ORDER SUMMARY */}
            <div className="lg:col-span-5">
              <div className="lg:sticky lg:top-28 space-y-5">
                <div className="overflow-hidden rounded-3xl border border-border bg-white shadow-lg">
                  <div className="bg-navy px-7 py-5">
                    <p className="text-xs font-bold uppercase tracking-[0.18em] text-emerald-300">Order summary</p>
                    <span className="mt-2 inline-flex items-center gap-2 rounded-full bg-lime/20 px-3 py-1 text-xs font-semibold text-lime">
                      <Check className="h-3 w-3" /> {win.label.toUpperCase()} PRICING · {win.note.replace("till ", "TILL ").replace("after ", "AFTER ").toUpperCase()}
                    </span>
                  </div>
                  <div className="space-y-3 px-7 py-6 text-sm">
                    <Row k={`${REG_TARIFF[passKey].label} × ${quantity}`} v={fmt(price.gross, currency)} />
                    {price.discount > 0 && (
                      <div className="flex items-center justify-between text-emeraldDeep">
                        <span className="font-medium">{price.tier.label} discount ({price.tier.discount}%)</span>
                        <span className="font-semibold">− {fmt(price.discount, currency)}</span>
                      </div>
                    )}
                    <div className="flex items-center justify-between border-t border-border pt-3">
                      <span className="text-navy/70">Subtotal</span>
                      <span className="font-medium text-navy">{fmt(price.net, currency)}</span>
                    </div>
                    {nationality === "indian" && <Row k="GST (18%)" v={fmt(price.gst, currency)} />}
                    <div className="flex items-center justify-between border-t border-border pt-3">
                      <span className="font-semibold text-navy">Total</span>
                      <span className="font-display text-2xl font-semibold text-navy" data-testid="summary-total">{fmt(price.total, currency)}</span>
                    </div>
                  </div>
                  <div className="px-7 pb-5 space-y-2 text-xs text-navy/60">
                    <p>{EVENT.dates}</p>
                    <p>{EVENT.venue}</p>
                    <p>Questions? <a href="mailto:events@teflas.com" className="font-semibold text-emeraldDeep hover:underline">events@teflas.com</a> · +91 98338 55566</p>
                  </div>
                  <div className="px-7 pb-7">
                    <button type="button" data-testid="reg-pay" onClick={handlePay} disabled={!valid || loading}
                      className={cn("flex w-full items-center justify-center gap-2 rounded-full bg-ethanolOrange px-7 py-4 text-sm font-bold text-navy transition-all hover:-translate-y-0.5 hover:bg-orangeDeep", (!valid || loading) && "opacity-50 hover:translate-y-0")}>
                      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />} Proceed to Payment · {fmt(price.total, currency)}
                    </button>
                    <div className="mt-4 space-y-2 text-xs text-navy/55">
                      <p className="flex items-center gap-2"><FileText className="h-3.5 w-3.5 text-emerald2" /> GST invoice issued on confirmation.</p>
                      <p className="flex items-center gap-2"><ShieldCheck className="h-3.5 w-3.5 text-emerald2" /> Includes conference, expo &amp; lunches, dinner &amp; access to the gala award show{passKey !== "conference" ? " + hotel stay" : ""}.</p>
                      {quantity > 1 && <p className="flex items-center gap-2"><Users className="h-3.5 w-3.5 text-emerald2" /> {quantity} delegate badges will be issued to the names below.</p>}
                    </div>
                  </div>
                </div>

                {/* PRICE RISES */}
                <div className="rounded-3xl border border-border bg-emerald2/5 p-6">
                  <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-emeraldDeep">
                    <Clock className="h-3.5 w-3.5" /> Price rises as the event nears
                  </div>
                  <div className="mt-4 space-y-2 text-sm">
                    {REG_WINDOW_LABELS.map((w, i) => {
                      const p = (nationality === "indian" ? REG_TARIFF[passKey].inr : REG_TARIFF[passKey].usd)[i];
                      const active = i === windowIdx;
                      return (
                        <div
                          key={w.label}
                          className={cn(
                            "flex items-center justify-between rounded-xl px-3 py-2.5",
                            active ? "bg-white border-2 border-emerald2 shadow-sm" : "border border-transparent"
                          )}
                        >
                          <div className={active ? "text-navy" : "text-navy/55"}>
                            <div className={cn("text-sm", active ? "font-semibold" : "font-medium")}>{w.label}{active ? "" : ""}</div>
                            <div className="text-xs text-navy/50">{i === 0 ? "Book on or before 31 Jul 2026" : i === 1 ? "1 Aug – 15 Aug 2026" : "After 15 Aug 2026 & spot registration"}</div>
                          </div>
                          <div className={cn("font-display text-base", active ? "font-bold text-navy" : "font-medium text-navy/55")}>{fmt(p, currency)}</div>
                        </div>
                      );
                    })}
                  </div>
                  <a
                    href="/assets/docs/bharat-ethanol-forum-sponsorship-proposal-2026.pdf"
                    target="_blank"
                    rel="noreferrer"
                    className="mt-5 flex w-full items-center justify-center gap-2 rounded-full border-2 border-navy bg-white px-5 py-3 text-xs font-bold uppercase tracking-wider text-navy transition hover:bg-navy hover:text-white"
                  >
                    <FileText className="h-3.5 w-3.5" /> Need approval? Download the rate card (PDF)
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
